/**
 * ErrorState — THE error renderer for learner-portal.
 *
 * One visual language (the compact, centred, typographic standard) for every
 * failure class. Full-page and inline mounts share this component; severity
 * decides the copy defaults and the CTA set, so CTAs can't vary per call site.
 *
 * Severity ladder (highest impact first) and its fixed actions:
 *  1. crash     — uncaught error, whole route is dead.   [Try again] [Back to home]
 *  2. failure   — blocking data failure, retry may help. [Try again] [Go back]? [Back to home]?
 *  3. not-found — dead end, retry never helps.           [Go back] [Back to home]
 *  4. inline    — section failed, page chrome intact.    [Try again]
 *
 * `page` mounts the same block centred in a full viewport (routes that render
 * nothing else); without it the block sizes to its container for inline use.
 * UIUX note: Back to home exists on full-page mounts because Retry can loop —
 * a user whose request keeps failing needs an exit, not a treadmill.
 */

import { v2 } from '@/lib/i18n/v2';
import styles from './error-state.module.less';

export type ErrorSeverity = 'crash' | 'failure' | 'not-found' | 'inline';

export interface ErrorStateProps {
  /** Drives copy defaults and which CTAs render. Default 'inline'. */
  severity?: ErrorSeverity;
  title?: string;
  subtitle?: string;
  /** Recovery action — renders the primary CTA (except severity not-found). */
  onRetry?: () => void;
  /** Back affordance when the mounting page knows where "back" is. */
  onGoBack?: () => void;
  /** Loop-breaker: always available as the tertiary exit on full-page mounts. */
  onHome?: () => void;
  /** Full-viewport centred mount for routes that render nothing else. */
  page?: boolean;
  /** Error object for the development-only details disclosure. */
  error?: Error | null;
}

const defaultTitle: Record<ErrorSeverity, string> = {
  crash: v2.error.crashTitle,
  failure: v2.error.defaultTitle,
  'not-found': v2.error.notFound('this page'),
  inline: v2.error.defaultTitle,
};

const defaultSubtitle: Partial<Record<ErrorSeverity, string>> = {
  crash: v2.error.subtitle,
  failure: v2.error.failureSubtitle,
  'not-found': v2.error.notFoundSubtitle,
};

export const ErrorState = ({
  severity = 'inline',
  title,
  subtitle,
  onRetry,
  onGoBack,
  onHome,
  page = false,
  error,
}: ErrorStateProps) => {
  // not-found never retries — retrying a dead link is the navigation loop
  // this component exists to prevent.
  const canRetry = severity !== 'not-found' && !!onRetry;
  return (
    <div className={`${styles.container} ${page ? styles.page : ''}`.trim()} role="alert">
      <p className={styles.title}>{title ?? defaultTitle[severity]}</p>
      <p className={styles.subtitle}>{subtitle ?? defaultSubtitle[severity]}</p>
      {canRetry && (
        <button className={styles.button} onClick={onRetry}>
          {v2.error.retry}
        </button>
      )}
      {onGoBack && (
        <button className={styles.secondaryButton} onClick={onGoBack}>
          {v2.error.goBack}
        </button>
      )}
      {onHome && (
        <button className={styles.homeLink} onClick={onHome}>
          {v2.error.backHome}
        </button>
      )}
      {import.meta.env.DEV && error && (
        <details className={styles.details}>
          <summary>Error Details (Development Only)</summary>
          <pre>
            {error.message}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}
    </div>
  );
};
